import { useEffect, useRef, useState } from "react";
import { sfx } from "@/lib/sfx";
import { Leaderboard } from "@/components/game/Leaderboard";
import { ClassScore } from "@/components/game/ClassScore";

export const turnOf = (state, id, name) => state.teams.find((t) => t.id === id) || (name ? { id: "solo", name, color: "#FACC15" } : null);

export const COLORS = ["#FACC15", "#3B82F6", "#22C55E", "#EF4444", "#A855F7", "#F97316", "#14B8A6", "#EC4899"];
export const pointColor = (v, settings) => {
  const levels = [...new Set((settings.slices || []).map(Number))].sort((x, y) => x - y);
  return settings.slice_colors?.[String(v)] || COLORS[Math.max(levels.indexOf(Number(v)), 0) % COLORS.length];
};
const isLight = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return ((n >> 16) * 299 + ((n >> 8) & 255) * 587 + (n & 255) * 114) / 1000 > 150;
};
const SPECIAL = { iflas: { fill: "#0B0F16", text: "#EF4444", label: "İFLAS" }, pas: { fill: "#F8FAFC", text: "#0B0F16", label: "PAS" } };
const R = 90;
const SPIN_MS = 5000;
const EASE = "cubic-bezier(0.165, 0.84, 0.44, 1)"; // easeOutQuart: p(t) = 1 - (1 - t)^4

const point = (deg, r) => [100 + r * Math.sin((deg * Math.PI) / 180), 100 - r * Math.cos((deg * Math.PI) / 180)];
const tickTimes = (dist, a) => Array.from({ length: Math.floor(dist / a) }, (_, k) => (SPIN_MS / 1000) * (1 - (1 - ((k + 1) * a) / dist) ** 0.25));

const textAngle = (deg, radial) => {
  if (radial) return deg > 180 ? deg + 90 : deg - 90;
  return deg > 90 && deg < 270 ? deg + 180 : deg;
};

const Slice = ({ i, a, value, color }) => {
  const sp = SPECIAL[value];
  const [x1, y1] = point(i * a, R);
  const [x2, y2] = point((i + 1) * a, R);
  const [tx, ty] = point((i + 0.5) * a, R * 0.64);
  const fill = sp?.fill || color;
  const dark = !sp && isLight(fill);
  return (
    <g>
      <path d={`M100 100 L${x1} ${y1} A${R} ${R} 0 ${a > 180 ? 1 : 0} 1 ${x2} ${y2} Z`} fill={fill} stroke="#0B0F16" strokeWidth="1.4" />
      <text x={tx} y={ty} fill={sp?.text || (dark ? "#0B0F16" : "#fff")} fontSize={sp ? 11 : 16} fontWeight="700" textAnchor="middle" dominantBaseline="middle"
        transform={`rotate(${textAngle((i + 0.5) * a, sp)} ${tx} ${ty})`} style={{ fontFamily: "Fredoka, sans-serif", letterSpacing: sp ? "0.08em" : 0 }}>{sp?.label || value}</text>
    </g>
  );
};

const Bulbs = ({ spinning }) => Array.from({ length: 24 }, (_, i) => {
  const [x, y] = point(i * 15, 96);
  return <circle key={i} cx={x} cy={y} r="2.1" fill={i % 2 ? "#FDE047" : "#FFF7D6"} className={spinning ? (i % 2 ? "anim-blink" : "anim-blink-alt") : ""} />;
});

const Pegs = ({ n, a }) => Array.from({ length: n }, (_, i) => {
  const [x, y] = point(i * a, R - 1);
  return <circle key={i} cx={x} cy={y} r="2" fill="#F8FAFC" stroke="#0B0F16" strokeWidth="0.6" />;
});

const Outcome = ({ spin, team, closed }) => {
  if (spin.value === "iflas") {
    return (
      <div className="anim-shake" data-testid="wheel-value">
        <p className="font-display text-[min(9rem,14vh)] font-bold leading-none text-red-500">İFLAS!</p>
        <p className="text-[3vh] text-slate-200 mt-[1vh]">{closed ? "Puan sıfırlandı" : <><b style={{ color: team?.color }}>{team?.name}</b> puanı sıfırlandı</>}{spin.lost ? ` (−${spin.lost})` : ""}</p>
      </div>
    );
  }
  if (spin.value === "pas") {
    return (
      <div className="anim-pop" data-testid="wheel-value">
        <p className="font-display text-[min(9rem,14vh)] font-bold leading-none text-white">PAS</p>
        <p className="text-[3vh] text-slate-200 mt-[1vh]">{closed ? "Çarkı yeniden çevirin." : "Sıra bir sonraki takıma geçti."}</p>
      </div>
    );
  }
  return <p className="font-display text-[min(10rem,16vh)] font-bold leading-none text-[#FACC15] anim-pop" data-testid="wheel-value">{spin.value} <span className="text-[5vh] text-slate-300">puan</span></p>;
};

export const WheelBoard = ({ state, send }) => {
  const slices = state.wheel_slices?.length ? state.wheel_slices : state.settings.slices;
  const empty = state.wheel_slices && state.wheel_slices.length === 0;
  const a = 360 / slices.length;
  const spin = state.spin;
  const [rot, setRot] = useState(spin ? 360 - (spin.index + 0.5) * a : 0);
  const [landed, setLanded] = useState(Boolean(spin && !spin.pending));
  const lastN = useRef(spin?.n);
  const rotRef = useRef(rot);
  const sound = state.settings.sound && state.settings.jingle !== "off";

  useEffect(() => {
    if (!spin?.pending || spin.n === lastN.current) return undefined;
    lastN.current = spin.n;
    setLanded(false);
    const prev = rotRef.current;
    const next = Math.ceil(prev / 360) * 360 + 360 * 5 + (360 - (spin.index + 0.5) * a);
    rotRef.current = next;
    setRot(next);
    if (sound) sfx.wheel(tickTimes(next - prev, a), SPIN_MS / 1000);
    const t = setTimeout(() => {
      setLanded(true);
      if (sound && spin.value === "iflas") sfx.bankrupt();
      if (sound && spin.value === "pas") sfx.pass();
    }, SPIN_MS + 100);
    return () => clearTimeout(t);
  }, [spin, a, sound]);

  const spinning = Boolean(spin?.pending) && !landed;
  const canSpin = !spin?.pending && !state.paused && Boolean(send) && !empty;
  const turnId = spin?.pending || (spin && landed && spin.value !== "pas") ? spin.turn_team : state.next_team;
  const team = spin?.turn_name && (spin.pending || landed) ? turnOf(state, null, spin.turn_name) : state.teams.find((t) => t.id === turnId);
  const spinTeam = turnOf(state, spin?.turn_team, spin?.turn_name);
  const closed = Boolean(state.settings.closed_session);
  const doSpin = () => canSpin && send({ type: "cmd", action: "spin" });

  return (
    <div className="h-full flex items-center justify-center gap-[5vw] relative" data-testid="wheel-board">
      <div className="relative" style={{ width: "66vh", height: "66vh" }}>
        <svg viewBox="0 0 40 30" className={`absolute left-1/2 -top-[3.5vh] -translate-x-1/2 z-10 w-[7vh] h-[5.5vh] origin-top drop-shadow-[0_4px_6px_rgba(0,0,0,0.6)] ${spinning ? "anim-flap" : ""}`} data-testid="wheel-pointer">
          <path d="M4 2 H36 L20 28 Z" fill="#FACC15" stroke="#0B0F16" strokeWidth="2" strokeLinejoin="round" />
        </svg>
        <svg viewBox="0 0 200 200" className="w-full h-full drop-shadow-[0_30px_60px_rgba(250,204,21,0.22)]" data-testid="wheel-svg">
          <circle cx="100" cy="100" r="99.5" fill="#1F2937" stroke="#FACC15" strokeWidth="1.5" />
          <Bulbs spinning={spinning} />
          <g style={{ transform: `rotate(${rot}deg)`, transformOrigin: "100px 100px", transition: `transform ${SPIN_MS}ms ${EASE}` }}>
            {slices.map((v, i) => <Slice key={i} i={i} a={a} value={v} color={pointColor(v, state.settings)} />)}
            <Pegs n={slices.length} a={a} />
          </g>
        </svg>
        <button onClick={doSpin} disabled={!canSpin} data-testid="wheel-spin-hub"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[24%] w-[24%] rounded-full bg-[#FACC15] text-[#1A1405] font-display font-bold text-[3.6vh] border-4 border-[#0B0F16] shadow-[0_6px_0_#A16207,0_0_40px_rgba(250,204,21,0.5)] hover:scale-105 active:translate-y-1 active:shadow-[0_2px_0_#A16207] disabled:opacity-90 disabled:cursor-default transition-transform duration-150">
          {spinning ? "…" : "ÇEVİR"}
        </button>
      </div>
      <div className="space-y-[3vh] min-w-[30vw] max-w-[40vw]">
        <h1 className="font-display text-[min(6rem,10vh)] font-bold tracking-[0.04em] neon-text-yellow" data-testid="wheel-title">ÇARKIFELEK</h1>
        {team && !closed && (
          <p className="text-[3.2vh] font-semibold" data-testid="wheel-turn">Sıra: <b className="font-display" style={{ color: team.color }}>{team.name}</b></p>
        )}
        {empty && !spin?.pending ? <p className="text-[3vh] text-[#FACC15]" data-testid="wheel-empty">Çarkta sorusu kalan puan yok. Öğretmen oyunu bitirebilir.</p> : spin && landed ? <Outcome spin={spin} team={spinTeam} closed={closed} /> : (
          <p className="text-[3vh] text-slate-300" data-testid="wheel-status">
            {spinning ? "Çark dönüyor…" : closed ? "Çarkı çevirmek için ÇEVİR'e basın." : team ? `${team.name} takımından bir öğrenci tahtaya gelip ÇEVİR'e bassın.` : state.solo ? "ÇEVİR'e basın: çark rastgele bir öğrenci seçecek!" : "Çarkı çevirmek için ÇEVİR'e basın."}
          </p>
        )}
        {spin && landed && spin.value !== "iflas" && spin.value !== "pas" && <p className="text-[2.4vh] text-slate-400">Soru açılıyor…</p>}
        {spin && landed && (spin.value === "iflas" || spin.value === "pas") && <p className="text-[2.4vh] text-slate-400">{closed ? "Çark yeniden çevrilebilir." : "Sıradaki takım çarkı çevirebilir."}</p>}
        {closed && <ClassScore score={state.class_total || 0} best={state.class_best} target={state.settings.class_target} />}
        {!closed && state.class_total !== undefined && <ClassScore mini score={state.class_total} best={state.class_best} />}
        {!closed && state.leaders?.length > 0 && <Leaderboard rows={state.leaders} title={state.solo ? "Liderlik tablosu" : "Takım yarışı"} highlight={spin?.turn_team} />}
      </div>
    </div>
  );
};
